export default class ItemParticle {
  // Core positioning and dimensions
  position: any;
  width: number = 0;
  height: number = 0;

  // Element metadata
  imageUrl: string = "";
  type: string = "";
  categoryIds: number[] = [];

  // Three.js rendering
  mesh: any;
  instancedMesh: any; // Instanced mesh for circles
  texture: any = null; // Loaded texture
  textureLoaded: boolean = false; // Track if texture has loaded

  // Physics (Matter.js)
  composite: any; // Matter.js composite for soft body
  circles: any[] = []; // Array of circle bodies
  constraints: any[] = []; // Array of spring constraints
  collisionGroup: number = 0; // Unique collision group for this particle

  // Position smoothing for jitter reduction
  private positionHistory: Array<{ x: number; y: number }>[] = []; // History of positions for each circle
  private readonly POSITION_HISTORY_SIZE = 5; // Number of frames to average (higher = smoother but more lag)
  private smoothingFactor: number = 0.3; // Simple lerp factor (0-1, lower = smoother, higher = more responsive)

  // Responsive scaling (2000px viewport as reference)
  private readonly REFERENCE_WIDTH = 2000;
  private scaleFactor: number = 1;
  private effectiveScale: number = 1; // Scale with minimum threshold for sizes

  // Physics configuration (base values at reference width)
  circleRadius: number = 30; // Radius of each circle
  springStiffness: number = 0.0; // Stiffness of springs
  springDamping: number = 1.2; // Damping of springs (increased for stability)
  margin: number = 50; // Edge margin in pixels
  collisionPadding: number = 50; // Padding around elements for collision detection
  maxVelocity: number = 15; // Maximum velocity to prevent jitter (pixels per frame)

  // Visual configuration
  visibleCircleVisibleMultiplier: number = 3.0; // Multiplier for visible circle radius

  // Noise animation
  noiseOffset: number = Math.random() * 1000; // Unique noise offset per particle
  noiseScale: number = 0.001; // Scale of noise sampling
  noiseStrength: number = 0.00015; // Strength of noise force
  noiseSpeed: number = 0.05; // Speed of noise evolution
  time: number = 0; // Time accumulator for animated noise

  // Hover state
  private hovered: boolean = false;
  private hoverOpacity: number = 0;
  private filterOpacity: number = 1; // Main canvas opacity based on filter matching

  // Debug visualization
  debug: boolean = false;
  debugMeshes: any[] = []; // Array of debug meshes for circles
  debugTextRect: any = null; // Debug rectangle for text position
  springLines: any[] = []; // Array of lines representing springs
  filterLines: any[] = []; // Array of lines from particle center to filters

  constructor(
    private element: Element,
    private three: any,
    private renderer: any,
    private scene: any,
    private noise: any, // SimplexNoise instance passed from FluidCanvas
    private matter: any, // Matter.js library
    private world: any // Matter.js world
  ) {
    this.position = new this.three.Vector3();
    // Create a unique negative collision group for this particle
    // Negative groups don't collide with each other within the same group
    this.collisionGroup = this.matter.Body.nextGroup(true);

    // Calculate responsive scale factor
    this.updateScaleFactor();

    this.initDOM();
    this.initPhysicsBody();
    this.initMesh();
  }

  public setHovered(hovered: boolean): void {
    this.hovered = hovered;
  }

  public isHovered(): boolean {
    return this.hovered;
  }

  public matchesAnyFilter(placedFilters: any[]): boolean {
    if (placedFilters.length === 0) return true;
    return placedFilters.some((filter) =>
      this.categoryIds.includes(filter.categoryId)
    );
  }

  public setNormalOpacity(): void {
    if (this.instancedMesh && this.instancedMesh.material) {
      this.instancedMesh.material.uniforms.opacity.value = 1.0;
      this.instancedMesh.material.needsUpdate = true;
    }
  }

  public updateFilterOpacity(targetOpacity: number): void {
    // Smooth fade transition for filter-based opacity
    const fadeSpeed = 0.1;
    this.filterOpacity += (targetOpacity - this.filterOpacity) * fadeSpeed;

    // Update material opacity if mesh exists
    if (this.instancedMesh && this.instancedMesh.material) {
      this.instancedMesh.material.uniforms.opacity.value = this.filterOpacity;
      this.instancedMesh.material.needsUpdate = true;
    }
  }

  public updateHoverOpacity(targetOpacity: number): void {
    // Smooth fade transition with different speeds for in/out
    const fadeInSpeed = 0.15;
    const fadeOutSpeed = 0.25;
    const fadeSpeed =
      targetOpacity > this.hoverOpacity ? fadeInSpeed : fadeOutSpeed;

    this.hoverOpacity += (targetOpacity - this.hoverOpacity) * fadeSpeed;

    // Update material opacity if mesh exists
    if (this.instancedMesh && this.instancedMesh.material) {
      this.instancedMesh.material.uniforms.opacity.value = this.hoverOpacity;
      this.instancedMesh.material.transparent = true;
      this.instancedMesh.material.needsUpdate = true;
    }
  }

  public updateScaleFactor(): void {
    this.scaleFactor = Math.min(1, window.innerWidth / this.REFERENCE_WIDTH);

    // Use a minimum scale factor of 0.5 for very small screens
    // This prevents orbs from becoming too tiny on mobile devices
    const minScale = 0.33;
    this.effectiveScale = Math.max(minScale, this.scaleFactor);

    // Apply scale to all size-related properties using effectiveScale
    this.circleRadius = 30 * this.effectiveScale;
    this.margin = 50 * this.effectiveScale;
    this.collisionPadding = 50 * this.effectiveScale;

    // Scale noise strength with square of scale factor to prevent aggressive movement on small screens
    // At 1000px width (scale 0.5): strength = 0.00015 * 0.25 = 0.0000375
    // At 2000px width (scale 1.0): strength = 0.00015 * 1.0 = 0.00015
    this.noiseStrength = 0.000025 * this.scaleFactor * this.scaleFactor;
  }

  private circleCircleIntersections(
    x1: number,
    y1: number,
    R1: number,
    x2: number,
    y2: number,
    R2: number
  ) {
    const dx = x2 - x1,
      dy = y2 - y1;
    const d = Math.hypot(dx, dy);
    if (d === 0 || d > R1 + R2 || d < Math.abs(R1 - R2)) return []; // no solution
    const a = (R1 * R1 - R2 * R2 + d * d) / (2 * d);
    const h2 = R1 * R1 - a * a;
    if (h2 < 0) return [];
    const xm = x1 + (a * dx) / d,
      ym = y1 + (a * dy) / d;
    const rx = (-dy * Math.sqrt(Math.max(0, h2))) / d;
    const ry = (dx * Math.sqrt(Math.max(0, h2))) / d;
    // two possible centers for the new circle
    return [
      { x: xm + rx, y: ym + ry },
      { x: xm - rx, y: ym - ry },
    ];
  }

  private overlapsAny(x: number, y: number, r: number, circles: any[]) {
    for (let i = 0; i < circles.length; i++) {
      const c = circles[i];
      const d2 = (x - c.x) ** 2 + (y - c.y) ** 2;
      if (d2 < (r + c.r) * (r + c.r) - 1e-9) return true;
    }
    return false;
  }

  private placeCircles(
    radii: number[],
    seed: { x: number; y: number; r: number } = { x: 0, y: 0, r: 40 }
  ) {
    const circles = [seed];

    for (const r of radii) {
      let placed = false;

      // 1) Try two-tangent with a few random (or nearby) pairs to find a fit
      for (let tries = 0; tries < 200 && !placed; tries++) {
        const i = Math.floor(Math.random() * circles.length);
        let j = Math.floor(Math.random() * circles.length);
        if (j === i) j = (j + 1) % circles.length;
        const A = circles[i],
          B = circles[j];
        const R1 = A.r + r,
          R2 = B.r + r;
        const pts = this.circleCircleIntersections(A.x, A.y, R1, B.x, B.y, R2);
        for (const p of pts) {
          if (!this.overlapsAny(p.x, p.y, r, circles)) {
            circles.push({ x: p.x, y: p.y, r });
            placed = true;
            break;
          }
        }
      }

      // 2) Fallback: single-tangent sweep around one neighbor
      if (!placed) {
        // pick a neighbor (nearest to origin as a heuristic)
        const base = circles.reduce(
          (best, c) =>
            c.x * c.x + c.y * c.y < best.x * best.x + best.y * best.y
              ? c
              : best,
          circles[0]
        );
        const R = base.r + r;
        // sweep angles (golden-step for uniform coverage)
        const phi = (Math.sqrt(5) - 1) / 2;
        for (let k = 0; k < 256 && !placed; k++) {
          const th = 2 * Math.PI * ((k * phi) % 1);
          const x = base.x + R * Math.cos(th);
          const y = base.y + R * Math.sin(th);
          if (!this.overlapsAny(x, y, r, circles)) {
            circles.push({ x, y, r });
            placed = true;
            break;
          }
        }
      }

      // 3) If still not placed, nudge outward (expand radius from a hull circle)
      if (!placed) {
        // as a last resort, push far from origin to find empty space
        const dir = Math.random() * 2 * Math.PI;
        let dist = 0;
        for (let step = 0; step < 200 && !placed; step++) {
          dist += r * 0.5;
          const x = Math.cos(dir) * dist,
            y = Math.sin(dir) * dist;
          if (!this.overlapsAny(x, y, r, circles)) {
            circles.push({ x, y, r });
            placed = true;
            break;
          }
        }
      }

      if (!placed) {
        console.warn("Could not place circle radius", r);
      }
    }

    return circles;
  }

  private initPhysicsBody(): void {
    const rect = this.element.getBoundingClientRect();
    const centerX = rect.left + this.width / 2;
    const centerY = -rect.top - this.height / 2;

    // Create circles around the perimeter of the text
    const paddedWidth = this.width + this.collisionPadding * 2;
    const paddedHeight = this.height + this.collisionPadding * 2;

    // Calculate number of circles based on perimeter
    const perimeter = 2 * (paddedWidth + paddedHeight);
    const numCircles = Math.max(
      8,
      Math.floor(perimeter / (this.circleRadius * 3))
    );

    // Create circles around the rectangle

    // Create a smaller, more stable core cluster (scaled)
    let startingRadii = (Math.random() * 40 + 20) * this.effectiveScale; // Smaller starting radius (20-60)
    let radii: number[] = [];
    let amountOfCircles = Math.floor(Math.random() * 2 + 3); // Fewer circles (3-6)
    for (let i = 0; i < amountOfCircles; i++) {
      const radius = (Math.random() * 30 + 25) * this.effectiveScale; // More consistent sizing (25-55, scaled)
      radii.push(radius);
    }

    const placedCircles = this.placeCircles(radii, {
      x: centerX,
      y: centerY,
      r: startingRadii,
    });

    // Store core circles separately
    const coreCircles: any[] = [];
    placedCircles.forEach((c) => {
      const circle = this.matter.Bodies.circle(
        c.x,
        c.y,
        c.r - 3 * this.effectiveScale,
        {
          friction: 0.5,
          frictionAir: 0.2,
          restitution: 0.1,
          density: 0.001,
          collisionFilter: {
            group: this.collisionGroup, // Circles in same group don't collide
          },
        }
      );
      this.circles.push(circle);
      coreCircles.push(circle);

      // Initialize position history for this circle
      this.positionHistory.push([{ x: c.x, y: c.y }]);
    });

    // FULLY triangulate core circles for maximum stability (no distance limit)
    for (let i = 0; i < coreCircles.length; i++) {
      for (let j = i + 1; j < coreCircles.length; j++) {
        const bodyA = coreCircles[i];
        const bodyB = coreCircles[j];
        const dx = bodyB.position.x - bodyA.position.x;
        const dy = bodyB.position.y - bodyA.position.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        const constraint = this.matter.Constraint.create({
          bodyA,
          bodyB,
          length: dist,
          stiffness: this.springStiffness,
          damping: this.springDamping,
        });
        this.constraints.push(constraint);
      }
    }

    // Find the outermost circle (furthest from center)
    let outerCircle = coreCircles[0];
    let maxDist = 0;
    coreCircles.forEach((circle) => {
      const dx = circle.position.x - centerX;
      const dy = circle.position.y - centerY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > maxDist) {
        maxDist = dist;
        outerCircle = circle;
      }
    });

    // Add a single tail extending from the outer circle
    const dx = outerCircle.position.x - centerX;
    const dy = outerCircle.position.y - centerY;
    const angle = Math.atan2(dy, dx); // Direction away from center

    const tailLength = Math.floor(Math.random() * 3 + 1); // 1-3 circles
    let prevCircle = outerCircle;

    for (let j = 0; j < tailLength; j++) {
      // Size decreases with distance (60% to 70% of previous)
      const sizeMultiplier = 0.6 + Math.random() * 0.1;
      const radius = prevCircle.circleRadius * sizeMultiplier;

      // Place at distance from previous circle (scaled)
      const distance =
        prevCircle.circleRadius + radius + 8 * this.effectiveScale;
      const x = prevCircle.position.x + Math.cos(angle) * distance;
      const y = prevCircle.position.y + Math.sin(angle) * distance;

      const tailCircle = this.matter.Bodies.circle(x, y, radius, {
        friction: 0.5,
        frictionAir: 0.3, // More air resistance for stability
        restitution: 0.1,
        density: 0.0003, // Even lighter
        collisionFilter: {
          group: this.collisionGroup, // Circles in same group don't collide
        },
      });

      this.circles.push(tailCircle);

      // Initialize position history for tail circle
      this.positionHistory.push([{ x, y }]);

      // Connect to previous circle in the chain
      const constraint = this.matter.Constraint.create({
        bodyA: prevCircle,
        bodyB: tailCircle,
        length: distance,
        stiffness: this.springStiffness * 0.6, // Looser connection
        damping: this.springDamping * 1.5, // More damping for stability
      });
      this.constraints.push(constraint);

      prevCircle = tailCircle;
    }

    // Create a composite to hold all circles and constraints
    this.composite = this.matter.Composite.create();
    this.matter.Composite.add(this.composite, this.circles);
    this.matter.Composite.add(this.composite, this.constraints);

    // Add composite to world
    this.matter.World.add(this.world, this.composite);
  }
  private initDOM(): void {
    // Temporarily remove not-active class to get accurate measurements
    const hadNotActive = this.element.classList.contains("not-active");
    if (hadNotActive) {
      this.element.classList.remove("not-active");
    }

    const rect = this.element.getBoundingClientRect();

    // Calculate 3vw/3vh offset to match camera extension
    const vwUnit = window.innerWidth / 100;
    const vhUnit = window.innerHeight / 100;
    const paddingX = 3 * vwUnit;
    const paddingY = 3 * vwUnit; // Camera uses vw for both directions

    // Apply offset to position to match extended camera bounds
    this.position.set(rect.left + paddingX, -rect.top - paddingY, 0);
    this.width = rect.width;
    this.height = rect.height; // Restore not-active class
    if (hadNotActive) {
      this.element.classList.add("not-active");
    }

    if (this.element instanceof HTMLElement) {
      requestAnimationFrame(() => {
        (this.element as HTMLElement).style.transform = `translate(${
          this.position.x
        }px, ${-this.position.y}px)`;
        this.element.classList.remove("not-active");
      });
    }

    this.imageUrl = this.element.getAttribute("data-image") || "";
    this.type = this.element.getAttribute("data-type") || "";
    this.categoryIds = (this.element.getAttribute("data-categories") || "")
      .split(",")
      .map((id) => parseInt(id))
      .filter((id) => !isNaN(id));
  }
  private initMesh(): void {
    // Load texture if imageUrl is available
    if (this.imageUrl) {
      const textureLoader = new this.three.TextureLoader();

      // Set timeout to mark as loaded after 10 seconds even if texture hasn't loaded
      const loadTimeout = setTimeout(() => {
        if (!this.textureLoaded) {
          console.warn(`Texture load timeout for: ${this.imageUrl}`);
          this.textureLoaded = true; // Mark as loaded to prevent blocking
          this.texture = null; // Clear texture reference
        }
      }, 10000);

      this.texture = textureLoader.load(
        this.imageUrl,
        (texture: any) => {
          // Success callback
          clearTimeout(loadTimeout);
          // Set color space to sRGB for proper color decoding
          // For Three.js r152+, use .colorSpace
          // if ("colorSpace" in texture) {
          //   texture.colorSpace = this.three.SRGBColorSpace;
          // } else {
          //   // For older Three.js versions, use .encoding
          //   texture.encoding = this.three.sRGBEncoding;
          // }
          // texture.magFilter = this.three.LinearFilter;
          // texture.minFilter = this.three.LinearMipMapLinearFilter;
          // texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
          texture.needsUpdate = true;
          this.textureLoaded = true;

          // Update material to use the loaded texture
          if (this.instancedMesh && this.instancedMesh.material) {
            this.instancedMesh.material.uniforms.uTexture.value = texture;
            this.instancedMesh.material.uniforms.hasTexture.value = true;
            this.instancedMesh.material.needsUpdate = true;
          }
        },
        undefined,
        (error) => {
          // Error callback
          clearTimeout(loadTimeout);
          console.error("Error loading texture:", this.imageUrl, error);
          this.textureLoaded = true; // Mark as loaded to prevent blocking
          this.texture = null; // Clear texture reference

          // Ensure material knows there's no texture
          if (this.instancedMesh && this.instancedMesh.material) {
            this.instancedMesh.material.uniforms.hasTexture.value = false;
            this.instancedMesh.material.needsUpdate = true;
          }
        }
      );
    } else {
      // No image URL, consider it "loaded" (will just render as colored blob)
      this.textureLoaded = true;
    }

    // Calculate bounding box of all circles
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    this.circles.forEach((circle) => {
      const r = circle.circleRadius * this.visibleCircleVisibleMultiplier;
      minX = Math.min(minX, circle.position.x - r);
      minY = Math.min(minY, circle.position.y - r);
      maxX = Math.max(maxX, circle.position.x + r);
      maxY = Math.max(maxY, circle.position.y + r);
    });
    const boundingBoxMin = new this.three.Vector2(minX, minY);
    const boundingBoxSize = new this.three.Vector2(maxX - minX, maxY - minY);

    // Create instanced mesh for all circles with shader material
    const geometry = new this.three.PlaneGeometry(2, 2); // Square plane, will create circle in shader

    // Custom shader material
    const vertexShader = `
      attribute vec2 uv;
      attribute vec3 position;
      attribute mat4 instanceMatrix;
      uniform mat4 modelViewMatrix;
      uniform mat4 projectionMatrix;
      uniform vec2 boundingBoxMin;
      uniform vec2 boundingBoxSize;
      varying vec2 vUv;
      varying vec2 vWorldUv; // UV relative to bounding box
      
      void main() {
        vUv = uv; // Local UV for circular mask
        
        // Calculate world position of this vertex
        vec4 worldPos = instanceMatrix * vec4(position, 1.0);
        
        // Map world position to UV space of full bounding box
        vec2 normalizedPos = (worldPos.xy - boundingBoxMin) / boundingBoxSize;
        
        // Calculate aspect ratio of bounding box
        float boundingBoxAspect = boundingBoxSize.x / boundingBoxSize.y;
        
        // Adjust UV coordinates to maintain aspect ratio
        // Center the UV and scale to fit within [0,1] while preserving aspect ratio
        if (boundingBoxAspect > 1.0) {
          // Bounding box is wider than tall
          vWorldUv = vec2(
            normalizedPos.x,
            (normalizedPos.y - 0.5) / boundingBoxAspect + 0.5
          );
        } else {
          // Bounding box is taller than wide
          vWorldUv = vec2(
            (normalizedPos.x - 0.5) * boundingBoxAspect + 0.5,
            normalizedPos.y
          );
        }
        
        vec4 mvPosition = modelViewMatrix * worldPos;
        gl_Position = projectionMatrix * mvPosition;
      }
    `;

    const fragmentShader = `
      precision highp float;
      
      uniform float opacity;
      uniform sampler2D uTexture;
      uniform bool hasTexture;
      uniform bool debugMode;
      uniform vec3 baseColor;
      varying vec2 vUv;
      varying vec2 vWorldUv;
    

      void main() {
      // Create circular mask using local UV
      vec2 center = vec2(0.5, 0.5);
      float dist = distance(vUv, center);
      if (dist > 0.5) discard;
      
      // Soft blur-like opacity falloff with increased contrast
      float blurRadius = 0.5;
      float softness = 0.2; // Reduced from 0.3 for sharper falloff
      float _opacity = 1.0 - smoothstep(blurRadius - softness, blurRadius, dist);
      // Increase contrast with power function
      _opacity = pow(_opacity, 0.7); // Values < 1.0 increase contrast
      
      // Apply debug mode opacity reduction
      if (debugMode) {
        _opacity *= 0.125;
      }
      
      if (hasTexture) {
        // Sample texture using world UV (relative to bounding box)
        vec4 texColor = texture2D(uTexture, vWorldUv);
        
        
        
        float finalAlpha = texColor.a * _opacity * opacity;
        gl_FragColor = vec4(texColor.rgb, finalAlpha);
      } else {
        gl_FragColor = vec4(baseColor, _opacity * opacity);
      }
      }
    `;

    const material = new this.three.RawShaderMaterial({
      uniforms: {
        opacity: { value: this.hoverOpacity },
        uTexture: { value: this.texture },
        hasTexture: { value: this.texture !== null },
        debugMode: { value: this.debug },
        baseColor: { value: new this.three.Color(0x5d5d5d) },
        boundingBoxMin: { value: boundingBoxMin },
        boundingBoxSize: { value: boundingBoxSize },
      },
      vertexShader,
      fragmentShader,
      transparent: true,
      premultipliedAlpha: false,
    });

    this.instancedMesh = new this.three.InstancedMesh(
      geometry,
      material,
      this.circles.length
    );

    // Disable frustum culling for instanced mesh with physics-based positioning
    // The bounding sphere would need to encompass all possible positions which defeats the purpose
    this.instancedMesh.frustumCulled = false;

    // Initialize instance matrices with position and scale
    const matrix = new this.three.Matrix4();
    const scale = new this.three.Vector3();
    this.circles.forEach((circle, i) => {
      scale.set(circle.circleRadius, circle.circleRadius, 1);
      matrix.compose(
        new this.three.Vector3(circle.position.x, circle.position.y, 0),
        new this.three.Quaternion(),
        scale.multiplyScalar(this.visibleCircleVisibleMultiplier)
      );
      this.instancedMesh.setMatrixAt(i, matrix);
    });
    this.instancedMesh.instanceMatrix.needsUpdate = true;

    this.scene.add(this.instancedMesh);

    if (this.debug) {
      // Create debug meshes for each circle
      let color = this.three.MathUtils.randInt(0, 0xffffff);

      this.circles.forEach((circle) => {
        const geometry = new this.three.CircleGeometry(circle.circleRadius, 16);
        const material = new this.three.MeshBasicMaterial({
          color: color,
          wireframe: true,
        });
        const mesh = new this.three.Mesh(geometry, material);
        mesh.position.set(circle.position.x, circle.position.y, 0);
        this.scene.add(mesh);
        this.debugMeshes.push(mesh);
      });

      // Create debug rectangle for text position - using simple plane mesh like the circles
      const rectGeometry = new this.three.PlaneGeometry(
        this.width,
        this.height
      );
      const rectMaterial = new this.three.MeshBasicMaterial({
        color: 0xff0000, // Red
        transparent: true,
        opacity: 0.3,
        side: this.three.DoubleSide,
      });
      this.debugTextRect = new this.three.Mesh(rectGeometry, rectMaterial);
      this.debugTextRect.position.set(
        this.position.x,
        this.position.y,
        0 // Same Z as circles
      );

      // Make absolutely sure it's visible
      this.debugTextRect.visible = true;
      this.debugTextRect.renderOrder = 999;
      // this.debugTextRect.frustumCulled = false; // Don't cull it

      this.scene.add(this.debugTextRect);

      // Create line meshes for each spring constraint
      this.constraints.forEach((constraint) => {
        const geometry = new this.three.BufferGeometry();
        const positions = new Float32Array(6); // 2 vertices * 3 coordinates
        geometry.setAttribute(
          "position",
          new this.three.BufferAttribute(positions, 3)
        );

        const material = new this.three.LineBasicMaterial({
          color: 0x00ff00,
          linewidth: 2,
        });

        const line = new this.three.Line(geometry, material);
        line.frustumCulled = false;
        this.scene.add(line);
        this.springLines.push(line);
      });
    }
  }
  update(
    particles: ItemParticle[],
    placedFilters: Array<{ categoryId: number; x: number; y: number }> = []
  ): void {
    // Increment time for animated noise
    this.time += 0.01;

    // Calculate center position of soft body
    let centerX = 0;
    let centerY = 0;
    this.circles.forEach((circle) => {
      centerX += circle.position.x;
      centerY += circle.position.y;
    });
    centerX /= this.circles.length;
    centerY /= this.circles.length;

    // Sample Perlin noise for this particle's position + time

    // Apply noise as force to each circle in the soft body
    this.circles.forEach((circle) => {
      const noiseX = this.noise(
        circle.position.x * this.noiseScale,
        circle.position.y * this.noiseScale,
        (this.time + this.noiseOffset) * this.noiseSpeed
      );
      const noiseY = this.noise(
        (circle.position.x + window.innerWidth) * this.noiseScale,
        (circle.position.y + window.innerHeight) * this.noiseScale,
        (this.time + this.noiseOffset) * this.noiseSpeed
      );

      this.matter.Body.applyForce(circle, circle.position, {
        x: noiseX * this.noiseStrength,
        y: noiseY * this.noiseStrength,
      });
    });

    // Only apply neighbor repulsion if not attracted to any category
    const hasActiveAttraction =
      placedFilters.length > 0 &&
      placedFilters.some((filter) =>
        this.categoryIds.includes(filter.categoryId)
      );

    if (!hasActiveAttraction) {
      // Apply gentle repulsion from nearby neighbors (scaled)
      let neighborRepulsionDistance = 1000 * this.effectiveScale; // Distance to check for neighbors (scaled)
      if (window.innerWidth < 500) {
        neighborRepulsionDistance = 550 * this.effectiveScale;
      }
      const neighborRepulsionStrength = 0.0028 * this.effectiveScale; // Gentle repulsion force (scaled)

      particles.forEach((otherParticle) => {
        if (otherParticle === this) return;

        // Calculate distance between particle centers
        const dx = centerX - otherParticle.position.x;
        const dy = centerY - otherParticle.position.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Apply repulsion if within range
        if (distance > 0 && distance < neighborRepulsionDistance) {
          // Force decreases with distance (stronger when closer)
          const force =
            neighborRepulsionStrength *
            (1 - distance / neighborRepulsionDistance);

          // Apply force to all circles in soft body
          this.circles.forEach((circle) => {
            this.matter.Body.applyForce(circle, circle.position, {
              x: (dx / distance) * force,
              y: (dy / distance) * force,
            });
          });
        }
      });
    }

    // Apply attraction/repulsion forces from category filters
    if (placedFilters.length > 0 && this.categoryIds.length > 0) {
      const attractionStrength = 0.018 * this.effectiveScale; // Base strength of attraction force (scaled)
      const repulsionStrength = 0.0025 * this.effectiveScale; // Strength of repulsion force (scaled)
      // Reduce repulsion distance on smaller screens (<1100px) for tighter clustering
      const baseRepulsionDistance = window.innerWidth < 1100 ? 500 : 1000;
      const repulsionDistance = baseRepulsionDistance * this.effectiveScale; // Distance for repulsion effect (scaled)

      // Clean up old filter lines if count changed
      if (this.debug && this.filterLines.length !== placedFilters.length) {
        this.filterLines.forEach((line) => this.scene.remove(line));
        this.filterLines = [];
      }

      // Calculate average attracted count for this particle across all matching filters
      const matchingFilters = placedFilters.filter((filter) =>
        this.categoryIds.includes(filter.categoryId)
      );

      // Boost attraction strength when matching multiple filters to help items settle between tags
      const multiFilterBoost =
        matchingFilters.length > 1
          ? 1.5 + (matchingFilters.length - 1) * 0.3
          : 1.0;

      let averageAttractedCount = 0;
      if (matchingFilters.length > 0) {
        let totalAttractedCount = 0;
        matchingFilters.forEach((filter) => {
          let count = 0;
          particles.forEach((particle) => {
            if (particle.categoryIds.includes(filter.categoryId)) {
              count++;
            }
          });
          totalAttractedCount += count;
        });
        averageAttractedCount = totalAttractedCount / matchingFilters.length;
      }

      // Calculate centroid of matching filters for multi-filter attraction
      let centroidX = 0;
      let centroidY = 0;
      if (matchingFilters.length > 0) {
        matchingFilters.forEach((filter) => {
          centroidX += filter.x;
          centroidY += -filter.y; // Invert Y for Three.js coordinate system
        });
        centroidX /= matchingFilters.length;
        centroidY /= matchingFilters.length;
      }

      // Scale falloff distance based on number of attracted particles (scaled)
      // More particles = larger falloff zone to prevent crowding and twitching
      // Use smaller base on smaller screens (<1100px) for tighter clustering around tags
      const isSmallScreen = window.innerWidth < 1100;
      const baseFalloff = isSmallScreen ? 60 : 200;
      const perParticleFalloff = isSmallScreen ? 20 : 50;
      const maxFalloff = isSmallScreen ? 500 : 2000;
      const attractionFalloffDistance = Math.min(
        baseFalloff * this.effectiveScale +
          averageAttractedCount * perParticleFalloff * this.effectiveScale,
        maxFalloff * this.effectiveScale
      );

      // Apply attraction toward centroid if matching multiple filters
      if (matchingFilters.length > 1) {
        this.circles.forEach((circle) => {
          const dx = centroidX - circle.position.x;
          const dy = centroidY - circle.position.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance > 1) {
            let force;
            if (distance > attractionFalloffDistance) {
              const distanceRatio = distance / 1000;
              force =
                attractionStrength *
                distanceRatio *
                distanceRatio *
                multiFilterBoost;
            } else {
              const falloffRatio = distance / attractionFalloffDistance;
              force =
                attractionStrength *
                (distance / 1000) *
                (falloffRatio * falloffRatio) *
                multiFilterBoost;
            }

            this.matter.Body.applyForce(circle, circle.position, {
              x: (dx / distance) * force,
              y: (dy / distance) * force,
            });
          }
        });
      }

      placedFilters.forEach((filter, filterIndex) => {
        const isMatchingCategory = this.categoryIds.includes(filter.categoryId);

        // Create debug line for this filter if needed
        if (this.debug && this.filterLines.length < placedFilters.length) {
          const geometry = new this.three.BufferGeometry();
          const positions = new Float32Array(6); // 2 points * 3 coords
          geometry.setAttribute(
            "position",
            new this.three.BufferAttribute(positions, 3)
          );
          const material = new this.three.LineBasicMaterial({
            color: 0x00ff00,
            transparent: true,
            opacity: 0.5,
          });
          const line = new this.three.Line(geometry, material);
          line.frustumCulled = false;
          this.scene.add(line);
          this.filterLines.push(line);
        }

        // Update debug line for this filter
        if (this.debug && this.filterLines[filterIndex]) {
          const line = this.filterLines[filterIndex];
          const geometry = line.geometry;
          const positions = geometry.attributes.position.array;

          // Line from particle center to filter
          positions[0] = centerX;
          positions[1] = centerY;
          positions[2] = 0;
          positions[3] = filter.x;
          positions[4] = -filter.y; // Invert Y for Three.js coordinate system
          positions[5] = 0;

          geometry.attributes.position.needsUpdate = true;

          // Color: green for attraction, red for repulsion
          const dx = filter.x - centerX;
          const dy = -filter.y - centerY; // Invert Y for Three.js coordinate system
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (isMatchingCategory) {
            line.material.color.setHex(0x00ff00); // Green for attraction
            line.material.opacity = 0.5;
          } else if (distance < repulsionDistance) {
            line.material.color.setHex(0xff0000); // Red for repulsion
            line.material.opacity = 0.5 * (1 - distance / repulsionDistance);
          } else {
            line.material.opacity = 0; // Hide if no force applied
          }
        }

        // Apply force to each circle in the soft body
        this.circles.forEach((circle) => {
          const dx = filter.x - circle.position.x;
          const dy = -filter.y - circle.position.y; // Invert Y for Three.js coordinate system
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance > 1) {
            // Only apply individual filter attraction for single-filter matches
            if (isMatchingCategory && matchingFilters.length === 1) {
              // ATTRACTION: Strong when far away, gentle when close
              let force;
              if (distance > attractionFalloffDistance) {
                // Far away: Exponential attraction that grows rapidly with distance
                // Using quadratic growth: force increases with square of distance
                const distanceRatio = distance / 1000;
                force =
                  attractionStrength *
                  distanceRatio *
                  distanceRatio *
                  multiFilterBoost;
              } else {
                // Close: Gentle attraction that decreases as we get closer
                // Use quadratic falloff for smoother deceleration
                const falloffRatio = distance / attractionFalloffDistance;
                force =
                  attractionStrength *
                  (distance / 1000) *
                  (falloffRatio * falloffRatio) *
                  multiFilterBoost;
              }

              this.matter.Body.applyForce(circle, circle.position, {
                x: (dx / distance) * force,
                y: (dy / distance) * force,
              });
            } else if (
              distance < repulsionDistance &&
              matchingFilters.length === 0
            ) {
              // REPULSION: Only push away items that don't match ANY placed filter
              // Force decreases with distance (stronger when closer)
              const force =
                repulsionStrength * (1 - distance / repulsionDistance);
              this.matter.Body.applyForce(circle, circle.position, {
                x: -(dx / distance) * force, // Negative for repulsion
                y: -(dy / distance) * force,
              });
            }
          }
        });
      });
    }

    // Clamp velocities to prevent jitter from excessive spring forces
    this.circles.forEach((circle) => {
      const velocityMag = Math.sqrt(
        circle.velocity.x * circle.velocity.x +
          circle.velocity.y * circle.velocity.y
      );
      if (velocityMag > this.maxVelocity) {
        const scale = this.maxVelocity / velocityMag;
        this.matter.Body.setVelocity(circle, {
          x: circle.velocity.x * scale,
          y: circle.velocity.y * scale,
        });
      }
    });

    // Gentle boundary forces (push back instead of hard walls)
    const vw = window.innerWidth / 100;
    const isMobile = window.innerWidth <= 768;
    const fadeZoneWidth = isMobile ? 0 : 5 * vw; // No fade on mobile, 5vw fade on desktop
    const minX = this.margin - fadeZoneWidth; // Start fade at -5vw (or at margin on mobile)
    const maxX = window.innerWidth - this.margin + fadeZoneWidth; // End fade at +5vw (or at margin on mobile)
    const minY = -this.margin;
    const baseBoundaryForceStrength = 0.0002 * this.effectiveScale; // Base push force (scaled)

    // Check if ANY circle is outside boundaries or in taper zone
    let leftPenetration = 0;
    let rightPenetration = 0;
    let topPenetration = 0;
    let minDistanceFromTop = Infinity;
    let minDistanceFromLeftEdge = Infinity;
    let minDistanceFromRightEdge = Infinity;

    // Downward taper force parameters
    let topTaperDistance = 200; // Extended from 200 to 450px for longer taper zone
    if (isMobile) {
      topTaperDistance = 100; // Shorter taper zone on mobile
    }
    const topTaperStrength = 0.025; // Increased from 0.0008 to 0.0025 for stronger push

    this.circles.forEach((circle) => {
      // Since Y coordinates are negative (inverted), distance from top is minY - circle.position.y
      // minY is the top boundary (negative value like -50)
      // Particles at the top have Y close to minY (e.g., -50)
      // As they move down, Y becomes more negative (e.g., -100, -200, etc.)
      const distanceFromTop = minY - circle.position.y;

      // Track minimum distance from top for taper force
      if (distanceFromTop < topTaperDistance && distanceFromTop > 0) {
        minDistanceFromTop = Math.min(minDistanceFromTop, distanceFromTop);
      }

      // Calculate distance from left and right edges for fade zones
      const leftEdge = this.margin;
      const rightEdge = window.innerWidth - this.margin;
      const distanceFromLeft = circle.position.x - leftEdge;
      const distanceFromRight = rightEdge - circle.position.x;

      // Track minimum distances for fade zones
      if (
        distanceFromLeft < fadeZoneWidth &&
        distanceFromLeft > -fadeZoneWidth
      ) {
        minDistanceFromLeftEdge = Math.min(
          minDistanceFromLeftEdge,
          Math.abs(distanceFromLeft)
        );
      }
      if (
        distanceFromRight < fadeZoneWidth &&
        distanceFromRight > -fadeZoneWidth
      ) {
        minDistanceFromRightEdge = Math.min(
          minDistanceFromRightEdge,
          Math.abs(distanceFromRight)
        );
      }

      // Track maximum penetration for each boundary
      if (circle.position.x < minX) {
        leftPenetration = Math.max(leftPenetration, minX - circle.position.x);
      }
      if (circle.position.x > maxX) {
        rightPenetration = Math.max(rightPenetration, circle.position.x - maxX);
      }
      if (circle.position.y > minY) {
        topPenetration = Math.max(topPenetration, circle.position.y - minY);
      }
    });

    // Apply forces to ALL circles if ANY circle triggers the condition
    this.circles.forEach((circle) => {
      // Apply downward taper force if ANY circle is in the taper zone
      if (minDistanceFromTop < topTaperDistance && minDistanceFromTop > 0) {
        // Use quadratic taper for smoother falloff (stronger at top, gentler deeper down)
        const taperRatio = 1 - minDistanceFromTop / topTaperDistance;
        const downwardForce = topTaperStrength * taperRatio * taperRatio;
        this.element.style.opacity = "0.5"; // Ensure fully visible when pushed down
        this.matter.Body.applyForce(circle, circle.position, {
          x: 0,
          y: -downwardForce,
        });
      }

      // Apply gradual left boundary force with fade (from -5vw to 5vw)
      if (minDistanceFromLeftEdge < fadeZoneWidth) {
        const fadeRatio = 1 - minDistanceFromLeftEdge / fadeZoneWidth;
        const boundaryForceStrength =
          baseBoundaryForceStrength * fadeRatio * fadeRatio;
        const leftEdge = this.margin;
        const distanceFromLeft = circle.position.x - leftEdge;

        if (distanceFromLeft < fadeZoneWidth) {
          const penetration = Math.max(0, fadeZoneWidth - distanceFromLeft);
          this.matter.Body.applyForce(circle, circle.position, {
            x: penetration * boundaryForceStrength,
            y: 0,
          });
        }
      }

      // Apply gradual right boundary force with fade (from -5vw to 5vw)
      if (minDistanceFromRightEdge < fadeZoneWidth) {
        const fadeRatio = 1 - minDistanceFromRightEdge / fadeZoneWidth;
        const boundaryForceStrength =
          baseBoundaryForceStrength * fadeRatio * fadeRatio;
        const rightEdge = window.innerWidth - this.margin;
        const distanceFromRight = rightEdge - circle.position.x;

        if (distanceFromRight < fadeZoneWidth) {
          const penetration = Math.max(0, fadeZoneWidth - distanceFromRight);
          this.matter.Body.applyForce(circle, circle.position, {
            x: -penetration * boundaryForceStrength,
            y: 0,
          });
        }
      }

      // Apply top boundary force (hard boundary)
      if (topPenetration > 0) {
        this.matter.Body.applyForce(circle, circle.position, {
          x: 0,
          y: -topPenetration * baseBoundaryForceStrength,
        });
      }
    });

    // Update position history and calculate smoothed positions
    const smoothedPositions: Array<{ x: number; y: number }> = [];
    this.circles.forEach((circle, i) => {
      // Add current position to history
      const history = this.positionHistory[i];
      history.push({ x: circle.position.x, y: circle.position.y });

      // Keep only recent history
      if (history.length > this.POSITION_HISTORY_SIZE) {
        history.shift();
      }

      // Calculate simple moving average
      let avgX = 0;
      let avgY = 0;
      history.forEach((pos) => {
        avgX += pos.x;
        avgY += pos.y;
      });
      avgX /= history.length;
      avgY /= history.length;

      // Apply linear interpolation for smoothing
      const smoothX =
        circle.position.x * this.smoothingFactor +
        avgX * (1 - this.smoothingFactor);
      const smoothY =
        circle.position.y * this.smoothingFactor +
        avgY * (1 - this.smoothingFactor);

      smoothedPositions.push({ x: smoothX, y: smoothY });
    });

    // Calculate center from smoothed positions
    let smoothCenterX = 0;
    let smoothCenterY = 0;
    smoothedPositions.forEach((pos) => {
      smoothCenterX += pos.x;
      smoothCenterY += pos.y;
    });
    smoothCenterX /= smoothedPositions.length;
    smoothCenterY /= smoothedPositions.length;

    // Update Three.js position from smoothed center
    this.position.set(smoothCenterX, smoothCenterY, 0);

    // Update debug text rectangle position (follows smoothed center)
    if (this.debug && this.debugTextRect) {
      this.debugTextRect.position.set(smoothCenterX, smoothCenterY, 0);
    }

    // Update instanced mesh positions using smoothed positions
    if (this.instancedMesh) {
      // Recalculate bounding box for smoothed positions
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      smoothedPositions.forEach((pos, i) => {
        const r =
          this.circles[i].circleRadius * this.visibleCircleVisibleMultiplier;
        minX = Math.min(minX, pos.x - r);
        minY = Math.min(minY, pos.y - r);
        maxX = Math.max(maxX, pos.x + r);
        maxY = Math.max(maxY, pos.y + r);
      });

      // Update bounding box uniforms
      const material = this.instancedMesh.material;
      material.uniforms.boundingBoxMin.value.set(minX, minY);
      material.uniforms.boundingBoxSize.value.set(maxX - minX, maxY - minY);

      const matrix = new this.three.Matrix4();
      const scale = new this.three.Vector3();
      smoothedPositions.forEach((pos, i) => {
        const circle = this.circles[i];
        scale.set(circle.circleRadius, circle.circleRadius, 1);
        matrix.compose(
          new this.three.Vector3(pos.x, pos.y, 0),
          new this.three.Quaternion(),
          scale.multiplyScalar(this.visibleCircleVisibleMultiplier)
        );
        this.instancedMesh.setMatrixAt(i, matrix);
      });
      this.instancedMesh.instanceMatrix.needsUpdate = true;
    }

    // Update debug meshes
    if (this.debug && this.debugMeshes.length > 0) {
      this.circles.forEach((circle, i) => {
        if (this.debugMeshes[i]) {
          this.debugMeshes[i].position.set(
            circle.position.x,
            circle.position.y,
            0
          );
        }
      });
    }

    // Update spring line visualizations with tension-based colors
    if (this.debug && this.springLines.length > 0) {
      this.constraints.forEach((constraint, i) => {
        if (this.springLines[i]) {
          const line = this.springLines[i];
          const geometry = line.geometry;
          const positions = geometry.attributes.position.array;

          // Update line endpoints
          positions[0] = constraint.bodyA.position.x;
          positions[1] = constraint.bodyA.position.y;
          positions[2] = 0;
          positions[3] = constraint.bodyB.position.x;
          positions[4] = constraint.bodyB.position.y;
          positions[5] = 0;

          geometry.attributes.position.needsUpdate = true;

          // Calculate spring tension (distance vs rest length)
          const dx = constraint.bodyB.position.x - constraint.bodyA.position.x;
          const dy = constraint.bodyB.position.y - constraint.bodyA.position.y;
          const currentLength = Math.sqrt(dx * dx + dy * dy);
          const restLength = constraint.length || currentLength;
          const tension = Math.abs(currentLength - restLength) / restLength;

          // Color based on tension: green (relaxed) -> yellow -> red (stretched)
          let color;
          if (tension < 0.1) {
            // Green to yellow
            const t = tension / 0.1;
            color = new this.three.Color(0, 1 - t * 0.5, 0);
          } else if (tension < 0.3) {
            // Yellow to orange
            const t = (tension - 0.1) / 0.2;
            color = new this.three.Color(1, 1 - t * 0.5, 0);
          } else {
            // Orange to red
            const t = Math.min((tension - 0.3) / 0.3, 1);
            color = new this.three.Color(1, 0.5 - t * 0.5, 0);
          }

          line.material.color = color;
        }
      });
    }

    // Update DOM element position to center of soft body
    if (this.element instanceof HTMLElement) {
      const vw = window.innerWidth / 100;
      const padding = 3 * vw;
      const usableHeight = window.innerHeight - 2 * padding;

      // Elegantly equivalent to original: normalize and map with clamp + lerp
      const t = this.three.MathUtils.clamp(
        -(centerY + window.scrollY + padding) / usableHeight,
        0,
        1
      );
      // Map t∈[0,1] to offset∈[-padding, +padding] (top -> -padding, bottom -> +padding)
      const offset = this.three.MathUtils.lerp(-padding, padding, t);

      // Calculate horizontal position with screen edge constraints
      let horizontalPos = centerX - this.width / 2;

      // Clamp horizontal position to keep text within screen bounds (with padding)
      const minX = padding;
      const maxX = window.innerWidth - this.width - padding;
      horizontalPos = this.three.MathUtils.clamp(horizontalPos, minX, maxX);

      this.element.style.transform = `translate(${horizontalPos}px, ${
        -centerY + offset - this.height / 2
      }px)`;
    }
  }

  /**
   * Recreate Three.js meshes after WebGL context loss.
   * This method removes old meshes and recreates them with the same geometry/material.
   */
  public recreateMeshes(scene: any): void {
    console.log(`Recreating mesh for item with ${this.circles.length} circles`);

    // Remove old mesh from scene if it exists
    if (this.instancedMesh && this.instancedMesh.parent) {
      this.instancedMesh.parent.remove(this.instancedMesh);
    }

    // DON'T dispose old resources - they're already invalid after context loss
    // Attempting to dispose them causes "object does not belong to this context" errors
    // Just clear references and let garbage collection handle them
    this.instancedMesh = null;
    this.texture = null;

    // Reinitialize the mesh with same parameters
    this.scene = scene;
    this.initMesh();

    console.log(
      `Mesh recreation complete, added to scene: ${this.instancedMesh !== null}`
    );
  }
}
